<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { prefersReducedMotion } from 'svelte/motion';
  import { DotLottie } from '@lottiefiles/dotlottie-web';

  interface Props {
    alIniciar: () => void;
  }

  let { alIniciar }: Props = $props();

  // Partículas interactivas táctiles / mouse
  let estelaParticulas = $state<{ id: number; x: number; y: number; size: number }[]>([]);
  let nextId = 0;

  // Control del gato Lottie
  let canvasGato = $state<HTMLCanvasElement | null>(null);
  let dotLottieInstance: DotLottie | null = null;
  let gatoVisible = $state(true); // Primera vez aparece solo
  let maullidoVisual = $state(false);
  let temporizadorOcultar: ReturnType<typeof setTimeout> | null = null;

  function activarGato(conSonidoEfecto = true) {
    gatoVisible = true;
    if (dotLottieInstance) {
      dotLottieInstance.stop();
      dotLottieInstance.play();
    }
    if (conSonidoEfecto) {
      maullidoVisual = true;
      setTimeout(() => {
        maullidoVisual = false;
      }, 1500);
    }

    if (temporizadorOcultar) clearTimeout(temporizadorOcultar);
    // Permanece visible unos segundos y luego se esconde suavemente entre la hierba
    temporizadorOcultar = setTimeout(() => {
      gatoVisible = false;
    }, 7000);
  }

  // Estado de precarga de recursos
  let progresoCarga = $state(0);
  let recursosListos = $state(false);

  const RECURSOS_IMAGENES = [
    '/images/girasol.png',
    '/images/fondo-ghibli.jpg',
    '/images/totoro-mira-arriba.png',
    '/images/placeholder.jpg',
    '/images/jazmin.png',
  ];
  const RUTA_AUDIO = '/audio/ghibli.mp3';

  async function precargarTodo() {
    let cargados = 0;
    const total = RECURSOS_IMAGENES.length + 1; // imágenes + audio

    const actualizar = () => {
      cargados++;
      progresoCarga = Math.round((cargados / total) * 100);
      if (cargados >= total) {
        setTimeout(() => {
          recursosListos = true;
        }, 400);
      }
    };

    // 1. Precargar imágenes en paralelo
    RECURSOS_IMAGENES.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = actualizar;
      img.onerror = actualizar;
    });

    // 2. Precargar audio en buffer / caché del navegador
    const audioPreload = new Audio();
    audioPreload.preload = 'auto';
    audioPreload.src = RUTA_AUDIO;
    const alCargarAudio = () => {
      audioPreload.removeEventListener('canplaythrough', alCargarAudio);
      audioPreload.removeEventListener('error', alCargarAudio);
      actualizar();
    };
    audioPreload.addEventListener('canplaythrough', alCargarAudio, { once: true });
    audioPreload.addEventListener('error', alCargarAudio, { once: true });
    audioPreload.load();

    // Timeout de seguridad de 7s por si la conexión es lenta para no bloquear a la usuaria
    setTimeout(() => {
      recursosListos = true;
      progresoCarga = 100;
    }, 7000);
  }

  onMount(() => {
    void precargarTodo();

    if (canvasGato) {
      dotLottieInstance = new DotLottie({
        canvas: canvasGato,
        src: 'https://lottie.host/df31ecd6-b3f6-4ce5-8712-6618e75f1df8/Fwulu8Mdgn.lottie',
        loop: true,
        autoplay: true,
      });
    }

    // Inicialmente visible por 8 segundos
    temporizadorOcultar = setTimeout(() => {
      gatoVisible = false;
    }, 8000);
  });

  onDestroy(() => {
    if (temporizadorOcultar) clearTimeout(temporizadorOcultar);
    dotLottieInstance?.destroy();
  });

  function agregarChispa(clientX: number, clientY: number) {
    if (prefersReducedMotion.current) return;
    const id = nextId++;
    const nueva = {
      id,
      x: clientX + (Math.random() * 20 - 10),
      y: clientY + (Math.random() * 20 - 10),
      size: Math.random() * 4 + 2,
    };
    estelaParticulas = [...estelaParticulas.slice(-15), nueva];

    setTimeout(() => {
      estelaParticulas = estelaParticulas.filter((p) => p.id !== id);
    }, 900);
  }

  function handlePointerMove(e: PointerEvent) {
    if (Math.random() > 0.45) return;
    agregarChispa(e.clientX, e.clientY);
  }

  function handleTouchMove(e: TouchEvent) {
    const touch = e.touches[0];
    if (touch) {
      agregarChispa(touch.clientX, touch.clientY);
    }
  }

  function verificarToqueZonaInferior(clientY: number, clientX: number) {
    const alturaVentana = window.innerHeight;
    const anchoVentana = window.innerWidth;
    // Si toca en el 35% inferior y dentro del 70% central de la pantalla
    const enZonaInferior = clientY > alturaVentana * 0.65;
    const enZonaCentral = clientX > anchoVentana * 0.15 && clientX < anchoVentana * 0.85;

    if (enZonaInferior && enZonaCentral) {
      activarGato(true);
    }
  }

  function handleScreenClick(e: MouseEvent) {
    verificarToqueZonaInferior(e.clientY, e.clientX);
  }

  function handleScreenTouch(e: TouchEvent) {
    const touch = e.touches[0];
    if (touch) {
      verificarToqueZonaInferior(touch.clientY, touch.clientX);
    }
  }
</script>

<div
  class="pantalla-ghibli"
  onpointermove={handlePointerMove}
  ontouchmove={handleTouchMove}
  onclick={handleScreenClick}
  ontouchstart={handleScreenTouch}
  role="presentation"
>
  <!-- Cielo crepuscular con halo central -->
  <div class="cielo-fondo"></div>
  <div class="halo-luz"></div>

  <!-- Estrellas y luciérnagas de preludio flotantes -->
  <div class="estrellas" aria-hidden="true">
    {#each Array(16) as _, i}
      <span
        class="estrella"
        style="
          --top: {Math.sin(i * 1.7) * 40 + 45}%;
          --left: {Math.cos(i * 2.3) * 45 + 50}%;
          --delay: {(i * 0.4).toFixed(1)}s;
          --duracion: {(2.5 + (i % 3) * 0.8).toFixed(1)}s;
        "
      ></span>
    {/each}
  </div>

  <div class="luciernagas-preludio" aria-hidden="true">
    {#each Array(6) as _, i}
      <div
        class="luciernaga-viva"
        style="
          --x-start: {15 + i * 14}%;
          --y-start: {50 + (i % 4) * 10}%;
          --dur: {7 + i * 1.5}s;
          --delay: {i * 1.2}s;
        "
      >
        <span class="nucleo"></span>
        <span class="aura"></span>
      </div>
    {/each}
  </div>

  <!-- Chispas interactivas al tocar/mover cursor -->
  <div class="estela-interactiva" aria-hidden="true">
    {#each estelaParticulas as chispa (chispa.id)}
      <span
        class="chispa"
        style="
          left: {chispa.x}px;
          top: {chispa.y}px;
          width: {chispa.size}px;
          height: {chispa.size}px;
        "
      ></span>
    {/each}
  </div>

  <!-- Contenido principal poético -->
  <main class="contenido-bienvenida">
    <div class="adorno-floral" aria-hidden="true">
      <svg viewBox="0 0 48 24" width="48" height="24" fill="none" stroke="currentColor">
        <path d="M24 12 C 18 4, 8 8, 2 12 C 8 16, 18 20, 24 12 Z" stroke-width="1.2" opacity="0.6" />
        <path d="M24 12 C 30 4, 40 8, 46 12 C 40 16, 30 20, 24 12 Z" stroke-width="1.2" opacity="0.6" />
        <circle cx="24" cy="12" r="2.5" fill="var(--color-luciernaga)" />
      </svg>
    </div>

    <div class="bloque-texto">
      <span class="subtitulo-estacion">Una noche de primavera</span>
      <h1 class="dedicatoria">Para mi Jazmín,</h1>
      <p class="frase-corazon">la flor de mi corazón</p>
    </div>

    <!-- Contenedor de acción / Preparando sorpresa -->
    <div class="contenedor-accion">
      {#if !recursosListos}
        <div class="bloque-preparando" aria-live="polite">
          <div class="indicador-sorpresa">
            <span class="icono-destello">✨</span>
            <span class="texto-sorpresa">Preparando sorpresa...</span>
            <span class="icono-destello">✨</span>
          </div>
          <div class="barra-progreso-track">
            <div class="barra-progreso-fill" style="width: {progresoCarga}%"></div>
          </div>
        </div>
      {:else}
        <button
          type="button"
          class="boton-despertar animacion-entrada-boton"
          onclick={alIniciar}
          aria-label="Despertar la noche y entrar al jardín"
        >
          <span class="luz-pulsante" aria-hidden="true"></span>
          <span class="texto-boton">Despertar la noche</span>
          <svg class="icono-brote" viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
            <path d="M10 2 C 10 7, 6 10, 2 10 C 6 10, 10 13, 10 18 C 10 13, 14 10, 18 10 C 14 10, 10 7, 10 2 Z" />
          </svg>
        </button>

        <div class="sugerencia-audio animacion-entrada-boton">
          <span class="icono-sonido">🎧</span>
          <span>Mejor con sonido</span>
        </div>
      {/if}
    </div>
  </main>

  <!-- Gato Lottie escondido/asomándose entre el pasto -->
  <div
    class="zona-gato"
    class:visible={gatoVisible}
    onclick={() => activarGato(true)}
    role="button"
    tabindex="0"
    onkeydown={(e) => e.key === 'Enter' && activarGato(true)}
    aria-label="Tocar al gatito"
  >
    {#if maullidoVisual}
      <div class="bocadillo-maullido" aria-hidden="true">
        <span>¡Miau miau!</span>
      </div>
    {/if}
    <div class="lienzo-gato-contenedor">
      <canvas bind:this={canvasGato} width="220" height="220" class="canvas-gato"></canvas>
    </div>
  </div>

  <!-- Siluetas de pasto y hierbas estilo colina Ghibli -->
  <div class="prado-siluetas" aria-hidden="true">
    <!-- Capa trasera -->
    <svg class="capa-hierba capa-fondo" viewBox="0 0 1200 240" preserveAspectRatio="none">
      <path
        d="M0,240 L0,110 Q 75,70 150,110 T 300,105 T 450,120 T 600,90 T 750,115 T 900,95 T 1050,115 T 1200,90 L1200,240 Z"
        fill="#0c1220"
      />
      <path
        d="M 120,110 Q 130,50 145,40 Q 135,70 130,110 M 420,115 Q 440,45 450,35 Q 440,75 435,115 M 780,105 Q 795,45 815,35 Q 800,75 790,105 M 1020,110 Q 1040,50 1060,40 Q 1045,75 1035,110"
        stroke="#0c1220"
        stroke-width="5"
        stroke-linecap="round"
        fill="none"
      />
    </svg>

    <!-- Capa frontal -->
    <svg class="capa-hierba capa-frente" viewBox="0 0 1200 240" preserveAspectRatio="none">
      <path
        d="M0,240 L0,140 Q 90,110 180,140 T 360,130 T 540,145 T 720,125 T 900,140 T 1080,130 T 1200,140 L1200,240 Z"
        fill="#070b14"
      />
      <path
        d="M 60,140 Q 75,70 95,55 Q 80,100 70,140 M 240,135 Q 260,65 285,50 Q 265,95 250,135 M 620,130 Q 640,60 670,45 Q 645,95 630,130 M 890,135 Q 910,65 930,50 Q 915,100 900,135 M 1110,135 Q 1125,75 1145,60 Q 1130,100 1120,135"
        stroke="#070b14"
        stroke-width="6"
        stroke-linecap="round"
        fill="none"
      />
    </svg>
  </div>
</div>

<style>
  .pantalla-ghibli {
    position: relative;
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
  }

  .cielo-fondo {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 70%,
      #29213d 0%,
      #1c1c36 40%,
      #101526 75%,
      #0a0e1a 100%
    );
    z-index: 0;
  }

  .halo-luz {
    position: absolute;
    bottom: 12%;
    left: 50%;
    transform: translateX(-50%);
    width: 80vw;
    height: 45vh;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      rgba(242, 177, 53, 0.12) 0%,
      rgba(154, 98, 166, 0.08) 50%,
      transparent 80%
    );
    pointer-events: none;
    z-index: 1;
    filter: blur(40px);
  }

  .estrellas {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
  }

  .estrella {
    position: absolute;
    top: var(--top);
    left: var(--left);
    width: 2px;
    height: 2px;
    border-radius: 50%;
    background: #fff9e6;
    box-shadow: 0 0 6px #fff4cc;
    animation: titilar var(--duracion) ease-in-out infinite;
    animation-delay: var(--delay);
  }

  @keyframes titilar {
    0%, 100% {
      opacity: 0.15;
      transform: scale(0.8);
    }
    50% {
      opacity: 0.85;
      transform: scale(1.4);
    }
  }

  .luciernagas-preludio {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  .luciernaga-viva {
    position: absolute;
    left: var(--x-start);
    top: var(--y-start);
    animation: flotarCurvo var(--dur) ease-in-out infinite alternate;
    animation-delay: var(--delay);
  }

  .nucleo {
    display: block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #fff;
    box-shadow:
      0 0 8px #ffe57f,
      0 0 16px #ffc107;
  }

  .aura {
    position: absolute;
    top: -6px;
    left: -6px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 235, 130, 0.4) 0%, transparent 70%);
    animation: respirarAura 2s ease-in-out infinite alternate;
  }

  @keyframes flotarCurvo {
    0% {
      transform: translate(0, 0);
    }
    33% {
      transform: translate(25px, -35px);
    }
    66% {
      transform: translate(-20px, -65px);
    }
    100% {
      transform: translate(15px, -110px);
      opacity: 0.3;
    }
  }

  @keyframes respirarAura {
    0% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    100% {
      transform: scale(1.5);
      opacity: 0.9;
    }
  }

  .estela-interactiva {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 10;
  }

  .chispa {
    position: absolute;
    border-radius: 50%;
    background: #fff6bd;
    box-shadow: 0 0 8px #ffdf73, 0 0 14px #ffb300;
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: desvanecerChispa 0.9s cubic-bezier(0.2, 0.8, 0.4, 1) forwards;
  }

  @keyframes desvanecerChispa {
    0% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.3);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -80%) scale(0.2);
    }
  }

  .contenido-bienvenida {
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--espaciado-l);
    max-width: 520px;
    margin-bottom: 5rem;
    animation: aparecerSuave 2.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes aparecerSuave {
    0% {
      opacity: 0;
      transform: translateY(24px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .adorno-floral {
    color: var(--color-luciernaga);
    margin-bottom: var(--espaciado-m);
    animation: pulsoFlor 4s ease-in-out infinite alternate;
  }

  @keyframes pulsoFlor {
    0% {
      opacity: 0.5;
      transform: scale(0.95);
    }
    100% {
      opacity: 0.95;
      transform: scale(1.05);
    }
  }

  .bloque-texto {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-bottom: var(--espaciado-xl);
  }

  .subtitulo-estacion {
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.28em;
    color: var(--color-luciernaga);
    opacity: 0.8;
    margin-bottom: 2px;
  }

  .dedicatoria {
    font-family: var(--familia-tipografica);
    font-size: clamp(2rem, 5vw, 2.75rem);
    font-weight: 400;
    color: #fff8eb;
    letter-spacing: 0.02em;
    margin: 0;
    text-shadow: 0 2px 16px rgba(255, 230, 150, 0.25);
  }

  .frase-corazon {
    font-style: italic;
    font-size: clamp(1.1rem, 3.5vw, 1.35rem);
    color: rgba(244, 234, 213, 0.85);
    margin: 0;
    letter-spacing: 0.03em;
  }

  .contenedor-accion {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--espaciado-m);
    min-height: 80px;
    justify-content: center;
  }

  /* Indicador de Preparando sorpresa */
  .bloque-preparando {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    animation: aparecerSuave 0.8s ease forwards;
  }

  .indicador-sorpresa {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: rgba(255, 235, 150, 0.08);
    border: 1px solid rgba(255, 235, 150, 0.25);
    border-radius: 9999px;
    backdrop-filter: blur(8px);
  }

  .texto-sorpresa {
    font-family: var(--familia-tipografica);
    font-size: 0.95rem;
    font-style: italic;
    color: #fff2c8;
    letter-spacing: 0.04em;
    animation: palpitarTexto 2s ease-in-out infinite alternate;
  }

  @keyframes palpitarTexto {
    0% { opacity: 0.7; }
    100% { opacity: 1; text-shadow: 0 0 10px rgba(255, 230, 140, 0.6); }
  }

  .icono-destello {
    font-size: 0.85rem;
    animation: girarDestello 3s linear infinite;
  }

  @keyframes girarDestello {
    0% { transform: rotate(0deg) scale(0.85); }
    50% { transform: rotate(180deg) scale(1.15); }
    100% { transform: rotate(360deg) scale(0.85); }
  }

  .barra-progreso-track {
    width: 170px;
    height: 4px;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 9999px;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
  }

  .barra-progreso-fill {
    height: 100%;
    background: linear-gradient(90deg, #f5b031, #ffe57f);
    border-radius: 9999px;
    transition: width 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    box-shadow: 0 0 8px rgba(255, 215, 100, 0.8);
  }

  .animacion-entrada-boton {
    animation: aparecerBotonListo 0.9s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes aparecerBotonListo {
    0% {
      opacity: 0;
      transform: scale(0.92) translateY(8px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .boton-despertar {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 18px 38px;
    background: linear-gradient(135deg, rgba(255, 243, 168, 0.12) 0%, rgba(217, 168, 92, 0.06) 100%);
    border: 1px solid rgba(255, 235, 150, 0.35);
    border-radius: 9999px;
    color: #fff9e6;
    font-family: var(--familia-tipografica);
    font-size: 1.125rem;
    letter-spacing: 0.06em;
    cursor: pointer;
    overflow: hidden;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.35),
      0 0 25px rgba(255, 215, 112, 0.15),
      inset 0 1px 1px rgba(255, 255, 255, 0.25);
    transition:
      transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.3s ease,
      border-color 0.3s ease;
  }

  .boton-despertar:hover {
    transform: scale(1.04);
    border-color: rgba(255, 235, 150, 0.7);
    box-shadow:
      0 6px 28px rgba(0, 0, 0, 0.4),
      0 0 35px rgba(255, 215, 112, 0.35),
      inset 0 1px 2px rgba(255, 255, 255, 0.4);
  }

  .boton-despertar:active {
    transform: scale(0.98);
  }

  .luz-pulsante {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: radial-gradient(circle, rgba(255, 240, 160, 0.25) 0%, transparent 70%);
    animation: pulsoBoton 2.8s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes pulsoBoton {
    0%, 100% {
      opacity: 0.3;
      transform: scale(0.95);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }

  .icono-brote {
    color: var(--color-luciernaga);
    transition: transform 0.3s ease;
  }

  .boton-despertar:hover .icono-brote {
    transform: rotate(15deg) scale(1.15);
  }

  .sugerencia-audio {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.825rem;
    color: rgba(244, 234, 213, 0.55);
    letter-spacing: 0.05em;
    animation: flotarSuave 3s ease-in-out infinite alternate;
  }

  @keyframes flotarSuave {
    0% {
      opacity: 0.4;
      transform: translateY(0);
    }
    100% {
      opacity: 0.8;
      transform: translateY(-3px);
    }
  }

  .prado-siluetas {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 180px;
    pointer-events: none;
    z-index: 3;
  }

  .capa-hierba {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 110%;
    height: 100%;
    transform-origin: bottom center;
  }

  .capa-fondo {
    opacity: 0.85;
    animation: brisaFondo 6s ease-in-out infinite alternate;
  }

  .capa-frente {
    opacity: 0.95;
    animation: brisaFrente 4.5s ease-in-out infinite alternate;
  }

  @keyframes brisaFondo {
    0% {
      transform: translateX(-3%) scaleY(0.98);
    }
    100% {
      transform: translateX(0%) scaleY(1.02);
    }
  }

  @keyframes brisaFrente {
    0% {
      transform: translateX(0%) scaleY(1.02);
    }
    100% {
      transform: translateX(-3%) scaleY(0.97);
    }
  }

  /* Gato Lottie en la base entre la hierba */
  .zona-gato {
    position: fixed;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%) translateY(120%);
    z-index: 4; /* situado entre la hierba trasera y frontal */
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition:
      transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1),
      opacity 0.6s ease;
    opacity: 0;
    pointer-events: auto;
  }

  .zona-gato.visible {
    transform: translateX(-50%) translateY(0%);
    opacity: 1;
  }

  .lienzo-gato-contenedor {
    width: 140px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 0 16px rgba(255, 235, 140, 0.3));
    transition: transform 0.25s ease;
  }

  .zona-gato:active .lienzo-gato-contenedor {
    transform: scale(0.92);
  }

  .canvas-gato {
    width: 140px !important;
    height: 140px !important;
    object-fit: contain;
  }

  .bocadillo-maullido {
    background: rgba(255, 248, 230, 0.92);
    color: #2b2418;
    font-size: 0.8rem;
    font-weight: bold;
    padding: 4px 10px;
    border-radius: 12px;
    margin-bottom: -4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    animation: saltito 0.4s ease-out;
  }

  @keyframes saltito {
    0% {
      transform: scale(0.6) translateY(6px);
      opacity: 0;
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>

